// benutzer-service
// verwaltet alle operationen für benutzer und benutzerprofile
// ermöglicht profil-verwaltung, rollen-verwaltung, punkte-verwaltung, passwort-änderung und account-löschung
import { doc, setDoc, getDoc, deleteDoc, collection, getDocs, query, orderBy } from 'firebase/firestore'
import { getAuth, updatePassword, deleteUser, reauthenticateWithCredential, EmailAuthProvider, sendEmailVerification } from 'firebase/auth'
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions'
import { getApp } from 'firebase/app'
import { getDb } from './firestore'
import { USE_EMULATOR, EMULATOR_PORTS } from '../config/firebase'

// funktion zum validieren eines benutzernamens
// erlaubt nur kleinbuchstaben, zahlen, unterstriche und punkte
export function validateUserName(name) {
  if (!name || typeof name !== 'string') {
    return { valid: false, error: 'Name ist erforderlich' }
  }
  
  // prüfe auf erlaubte zeichen: nur a-z, 0-9, _, .
  const validPattern = /^[a-z0-9._]+$/
  if (!validPattern.test(name)) {
    return { 
      valid: false, 
      error: 'Name darf nur Kleinbuchstaben, Zahlen, Unterstriche (_) und Punkte (.) enthalten' 
    }
  }
  
  // prüfe minimale länge
  if (name.length < 1) {
    return { valid: false, error: 'Name muss mindestens 1 Zeichen lang sein' }
  }
  
  // prüfe maximale länge
  if (name.length > 50) {
    return { valid: false, error: 'Name darf maximal 50 Zeichen lang sein' }
  }
  
  return { valid: true }
}

// funktion zum normalisieren eines namens aus einer e-mail-adresse
// extrahiert den teil vor dem @ und normalisiert ihn
function normalizeNameFromEmail(email) {
  if (!email || typeof email !== 'string') {
    return 'user'
  }
  
  // extrahiere teil vor dem @
  let name = email.split('@')[0].toLowerCase()
  
  // entferne alle ungültigen zeichen und ersetze sie durch unterstriche
  name = name.replace(/[^a-z0-9._]/g, '_')
  
  // entferne mehrfache unterstriche
  name = name.replace(/_+/g, '_')
  
  // entferne führende und nachfolgende unterstriche
  name = name.replace(/^_+|_+$/g, '')
  
  // wenn leer, verwende 'user'
  if (!name) {
    name = 'user'
  }
  
  return name
}

// funktion zum prüfen ob ein benutzername bereits existiert
export async function userNameExists(name, excludeUserId = null) {
  try {
    if (!name || typeof name !== 'string') {
      return false
    }
    
    const db = getDb()
    const usersRef = collection(db, 'user')
    const querySnapshot = await getDocs(usersRef)
    
    // normalisiere den zu prüfenden namen (kleinschreibung, trim)
    const normalizedName = name.trim().toLowerCase()
    
    for (const docSnapshot of querySnapshot.docs) {
      // überspringe den aktuellen benutzer wenn excludeUserId angegeben
      if (excludeUserId && docSnapshot.id === excludeUserId) {
        continue
      }
      
      const userData = docSnapshot.data()
      
      // überspringe gelöschte benutzer
      if (userData.deleted) {
        continue
      }
      
      // prüfe ob name bereits existiert (case-insensitive)
      if (userData.name) {
        const existingName = userData.name.trim().toLowerCase()
        if (existingName === normalizedName) {
          console.log(`Username "${name}" exists (found in user ${docSnapshot.id})`)
          return true
        }
      }
    }
    
    console.log(`Username "${name}" is available`)
    return false
  } catch (error) {
    console.error('Error checking if username exists:', error)
    // bei fehler annehmen dass name existiert (sicherheitshalber)
    return true
  }
}

// funktion zum generieren eines eindeutigen benutzernamens
// fügt zahlen hinzu wenn name bereits existiert
export async function generateUniqueUserName(baseName, excludeUserId = null) {
  // validiere und normalisiere den basis-namen
  const normalized = normalizeNameFromEmail(baseName)
  let name = normalized
  let counter = 1
  
  // prüfe ob name bereits existiert
  while (await userNameExists(name, excludeUserId)) {
    // füge zahlen hinzu bis ein eindeutiger name gefunden wird
    name = `${normalized}${counter}`
    counter++
    
    // verhindere endlose schleife
    if (counter > 1000) {
      // fallback: verwende timestamp
      name = `${normalized}_${Date.now()}`
      break
    }
  }
  
  return name
}

// funktion zum speichern des benutzerprofils in firestore
// speichert name, rolle (false = schüler, true = admin), punkte und zeitstempel
// email ist optional und wird nur gespeichert wenn angegeben
export async function saveUserProfile(userId, name, role = false, points = 0, email = null) {
  // prüfe ob eine benutzer-id angegeben wurde
  if (!userId) {
    console.error("Kein Benutzer-ID angegeben.")
    return
  }
  
  // validiere benutzername
  const nameValidation = validateUserName(name)
  if (!nameValidation.valid) {
    throw new Error(nameValidation.error)
  }

  // hole die firestore-datenbankinstanz
  const db = getDb()
  // referenz zum benutzer-dokument
  const userRef = doc(db, "user", userId)

  try {
    // erstelle daten-objekt
    const userData = {
      name: name,
      role: role, // boolean: false = schüler, true = admin
      points: points,
      updatedAt: new Date().toISOString() // aktualisierungsdatum
    }
    
    // füge email hinzu wenn angegeben
    if (email) {
      userData.email = email
    }
    
    // hole das aktuelle dokument um createdAt zu behalten
    const currentDoc = await getDoc(userRef)
    if (!currentDoc.exists()) {
      // nur beim erstmaligen erstellen createdAt setzen
      userData.createdAt = new Date().toISOString()
    }
    
    // speichere die benutzerdaten (merge: true bedeutet, dass vorhandene felder erhalten bleiben)
    await setDoc(userRef, userData, { merge: true })
    console.log("Benutzerprofil erfolgreich gespeichert!")
    return true
  } catch (error) {
    console.error("Fehler beim Speichern des Benutzerprofils:", error)
    throw error
  }
}

// funktion zum speichern der e-mail-adresse in firestore, falls noch nicht vorhanden
// wird beim login aufgerufen, um bestehende profile zu aktualisieren
export async function updateEmailInFirestore(userId, email) {
  if (!userId || !email) return
  
  try {
    const db = getDb()
    const userRef = doc(db, "user", userId)
    const userSnap = await getDoc(userRef)
    
    // nur aktualisieren wenn das dokument existiert und keine e-mail hat
    if (userSnap.exists() && !userSnap.data().email) {
      await setDoc(userRef, { 
        email: email,
        updatedAt: new Date().toISOString()
      }, { merge: true })
      console.log('E-Mail-Adresse in Firestore gespeichert:', email)
    }
  } catch (error) {
    console.warn('Could not update email in Firestore:', error)
  }
}

// funktion zum abrufen eines benutzerprofils aus firestore
// gibt das profil mit allen daten zurück oder null wenn nicht gefunden
export async function getUserProfile(userId) {
  try {
    // hole die firestore-datenbankinstanz
    const db = getDb()
    // referenz zum benutzer-dokument
    const userRef = doc(db, "user", userId)
    // hole das dokument
    const userSnap = await getDoc(userRef)
    
    // wenn das dokument existiert, gib es zurück
    if (userSnap.exists()) {
      return { id: userSnap.id, ...userSnap.data() }
    }
    // wenn nicht gefunden, gib null zurück
    return null
  } catch (error) {
    console.error('Error getting user profile:', error)
    throw error
  }
}

// funktion zum abrufen des profils des aktuell angemeldeten benutzers
// erstellt automatisch ein profil, wenn keines existiert
export async function getCurrentUserProfile() {
  // hole die firebase auth instanz
  const auth = getAuth()
  // hole den aktuell angemeldeten benutzer
  const user = auth.currentUser
  
  // wenn kein benutzer angemeldet ist, gib null zurück
  if (!user) {
    return null
  }
  
  // versuche das profil aus firestore abzurufen
  let profile = await getUserProfile(user.uid)
  
  // wenn kein profil existiert (z.b. wurde firestore-dokument gelöscht),
  // versuche displayName aus firebase auth zu verwenden
  if (!profile) {
    // lade user neu, um sicherzustellen, dass displayName verfügbar ist
    try {
      await user.reload()
    } catch (reloadError) {
      console.warn('Could not reload user (non-critical):', reloadError)
    }
    
    // verwende displayName aus firebase auth (sollte beim registrieren gesetzt worden sein)
    let userName = user.displayName
    console.log('🔍 getCurrentUserProfile: displayName aus Firebase Auth:', userName)
    console.log('🔍 getCurrentUserProfile: E-Mail:', user.email)
    
    if (!userName) {
      // fallback: generiere einen sinnvollen namen aus der e-mail-adresse
      // statt einfach 'user' zu verwenden
      console.warn('⚠️ displayName nicht gefunden, generiere Namen aus E-Mail')
      userName = await generateUniqueUserName(user.email || 'user')
    }
    
    console.log('✅ getCurrentUserProfile: Verwende Benutzername:', userName)
    // erstelle neues profil mit standardwerten (schüler, 0 punkte)
    await saveUserProfile(user.uid, userName, false, 0, user.email || null)
    // hole das neu erstellte profil
    profile = await getUserProfile(user.uid)
  }
  
  // gib das profil zurück
  return profile
}


// funktion zum reaktivieren eines benutzers (entfernt deleted-flag)
// wird beim login aufgerufen, damit gelöschte benutzer wieder sichtbar werden
export async function reactivateUser(userId) {
  try {
    // hole die firestore-datenbankinstanz
    const db = getDb()
    // referenz zum benutzer-dokument
    const userRef = doc(db, "user", userId)
    
    // prüfe ob dokument existiert
    const userSnap = await getDoc(userRef)
    if (!userSnap.exists()) {
      // wenn dokument nicht existiert, ist nichts zu reaktivieren
      return false
    }
    
    const userData = userSnap.data()
    
    // prüfe ob benutzer als gelöscht markiert ist
    if (userData.deleted) {
      // entferne deleted-flag und deletedAt-feld
      await setDoc(userRef, {
        deleted: false,
        deletedAt: null,
        reactivatedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }, { merge: true })
      
      console.log(`✅ User document ${userId} reactivated`)
      return true
    }
    
    // benutzer war nicht gelöscht, nichts zu tun
    return false
  } catch (error) {
    console.error('❌ Error reactivating user:', error)
    console.error('Error code:', error.code)
    console.error('Error message:', error.message)
    throw error
  }
}

// funktion zum abrufen der firebase functions instanz
// verbindet automatisch mit dem emulator im entwicklungsmodus
function getFunctionsInstance() {
  const app = getApp()
  const functions = getFunctions(app, 'us-central1')
  
  // verbinde mit functions emulator wenn konfiguriert
  if (USE_EMULATOR) {
    try {
      // prüfe ob bereits verbunden (verhindert fehler bei hot reload)
      const isAlreadyConnected = functions._delegate?._url?.includes('localhost') ||
                                  functions._delegate?._url?.includes('127.0.0.1')
      
      // wenn noch nicht verbunden, verbinde mit emulator
      if (!isAlreadyConnected) {
        connectFunctionsEmulator(functions, 'localhost', EMULATOR_PORTS.functions)
        console.log(`✅ Verbunden mit Firebase Functions Emulator (localhost:${EMULATOR_PORTS.functions})`)
      }
    } catch (error) {
      // wenn emulator nicht verfügbar, verwende cloud (nur warnung, kein fehler)
      console.warn('⚠️ Functions Emulator nicht verfügbar, verwende Firebase Cloud:', error.message)
    }
  }
  
  return functions
}


// funktion zum vollständigen löschen eines benutzers
// löscht sowohl den firebase auth account als auch das firestore-dokument
// verwendet cloud function für sichere löschung (nur admins können löschen)
export async function deleteUserCompletely(userId) {
  try {
    console.log(`Attempting to completely delete user: ${userId}`)
    
    // hole die firebase functions instanz
    const functions = getFunctionsInstance()
    
    // erstelle eine callable function referenz
    const deleteUserFunction = httpsCallable(functions, 'deleteUserCompletely')
    
    // rufe die cloud function auf
    const result = await deleteUserFunction({ userId })
    
    console.log('Delete user result:', result.data)
    
    // gib das ergebnis zurück
    return result.data
  } catch (error) {
    console.error('Error deleting user completely:', error)
    
    // übersetze fehlercodes in benutzerfreundliche nachrichten
    if (error.code) {
      const errorMessages = {
        'permission-denied': 'Sie haben keine Berechtigung für diese Aktion.',
        'not-found': 'Benutzer nicht gefunden.',
        'unauthenticated': 'Sie müssen angemeldet sein, um diese Aktion auszuführen.',
        'failed-precondition': 'Sie können sich nicht selbst löschen.',
        'invalid-argument': 'Ungültige Anfrage. Bitte versuchen Sie es erneut.'
      }
      
      const message = errorMessages[error.code] || error.message || 'Ein Fehler ist aufgetreten.'
      throw new Error(message)
    }
    
    // wenn error.message existiert, verwende es
    if (error.message) {
      throw new Error(error.message)
    }
    
    throw new Error('Ein unerwarteter Fehler ist aufgetreten.')
  }
}

// funktion zum abrufen aller benutzer aus firestore
// gibt alle benutzer sortiert nach erstellungsdatum zurück (neueste zuerst)
// filtert gelöschte benutzer aus (soft delete)
export async function getAllUsers() {
  try {
    // hole die firestore-datenbankinstanz
    const db = getDb()
    // referenz zur 'user' collection
    const usersRef = collection(db, 'user')
    // query: sortiere nach createdAt, absteigend (neueste zuerst)
    const q = query(usersRef, orderBy('createdAt', 'desc'))
    
    // führe die query aus
    const querySnapshot = await getDocs(q)
    const users = []
    
    // durchlaufe alle dokumente und füge sie zum array hinzu
    querySnapshot.forEach((doc) => {
      const userData = doc.data()
      // filtere gelöschte benutzer aus (soft delete)
      if (!userData.deleted) {
        users.push({
          id: doc.id, // dokument-id
          ...userData // alle daten des dokuments
        })
      }
    })
    
    // gib das array mit allen benutzern zurück (ohne gelöschte)
    return users
  } catch (error) {
    console.error('Fehler beim Abrufen der Benutzer:', error)
    throw error
  }
}

// funktion zum aktualisieren von benutzerdaten (name, rolle, punkte)
// aktualisiert nur die angegebenen felder und verwaltet tägliche punkte
export async function updateUserData(userId, name, role, points) {
  try {
    // hole die firestore-datenbankinstanz
    const db = getDb()
    // referenz zum benutzer-dokument
    const userRef = doc(db, "user", userId)
    // hole das aktuelle dokument
    const userSnap = await getDoc(userRef)
    
    // objekt für die zu aktualisierenden daten
    const updateData = {
      updatedAt: new Date().toISOString() // immer aktualisieren
    }
    
    // wenn name angegeben wurde, füge ihn hinzu
    if (name !== undefined && name !== null) {
      updateData.name = name
    }
    
    // wenn rolle angegeben wurde, füge sie hinzu
    if (role !== undefined && role !== null) {
      updateData.role = role
    }
    
    // wenn punkte angegeben wurden, aktualisiere sie und tägliche punkte
    if (points !== undefined && points !== null) {
      // berechne die differenz zwischen neuen und aktuellen punkten
      const currentPoints = userSnap.exists() ? (userSnap.data().points || 0) : 0
      const pointsDifference = points - currentPoints
      
      updateData.points = points
      
      // aktualisiere tägliche punkte wenn sich punkte geändert haben
      if (pointsDifference !== 0) {
        // hole das heutige datum als string (yyyy-mm-dd)
        const today = new Date().toISOString().split('T')[0]
        const userData = userSnap.exists() ? userSnap.data() : {}
        const dailyPoints = userData.dailyPoints || {}
        const todayPoints = dailyPoints[today] || 0
        // addiere die differenz zu den heutigen punkten (kann negativ sein wenn punkte reduziert werden)
        dailyPoints[today] = todayPoints + pointsDifference
        
        // behalte nur die letzten 30 tage der täglichen punkte
        const thirtyDaysAgo = new Date()
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
        const filteredDailyPoints = {}
        for (const [date, pts] of Object.entries(dailyPoints)) {
          if (date >= thirtyDaysAgo.toISOString().split('T')[0]) {
            filteredDailyPoints[date] = pts
          }
        }
        
        updateData.dailyPoints = filteredDailyPoints
      }
    }
    
    // speichere die aktualisierten daten
    await setDoc(userRef, updateData, { merge: true })
    
    // gib das aktualisierte profil zurück
    return await getUserProfile(userId)
  } catch (error) {
    console.error('Error updating user data:', error)
    throw error
  }
}

// funktion zum aktualisieren des benutzernamens
// aktualisiert nur den namen, behält rolle und punkte bei
export async function updateUserName(newName) {
  try {
    // hole die firebase auth instanz
    const auth = getAuth()
    // hole den aktuell angemeldeten benutzer
    const user = auth.currentUser
    
    // prüfe ob ein benutzer angemeldet ist
    if (!user) {
      throw new Error('Kein Benutzer angemeldet')
    }
    
    // hole das aktuelle profil
    const currentProfile = await getCurrentUserProfile()
    if (!currentProfile) {
      throw new Error('Benutzerprofil nicht gefunden')
    }
    
    // prüfe ob der neue name bereits existiert (außer für den aktuellen benutzer)
    const nameExists = await userNameExists(newName, user.uid)
    if (nameExists) {
      throw new Error('Dieser Benutzername ist bereits vergeben.')
    }
    
    // aktualisiere auch den displayName in firebase auth
    const { updateProfile } = await import('firebase/auth')
    try {
      await updateProfile(user, {
        displayName: newName
      })
      // lade user neu, damit displayName verfügbar ist
      await user.reload()
      console.log('✅ displayName in Firebase Auth aktualisiert:', newName)
    } catch (profileError) {
      console.warn('Could not update displayName in Firebase Auth (non-critical):', profileError)
      // fehler ist nicht kritisch, fahre fort
    }
    
    // speichere das profil mit neuem namen, aber gleicher rolle und punkten
    await saveUserProfile(
      user.uid,
      newName,
      currentProfile.role,
      currentProfile.points || 0
    )
    
    return true
  } catch (error) {
    console.error('Error updating user name:', error)
    throw error
  }
}

// funktion zum ändern des passworts
// benötigt das aktuelle passwort zur bestätigung (re-authentifizierung)
export async function changePassword(currentPassword, newPassword) {
  try {
    // hole die firebase auth instanz
    const auth = getAuth()
    // hole den aktuell angemeldeten benutzer
    const user = auth.currentUser
    
    // prüfe ob ein benutzer angemeldet ist und eine email hat
    if (!user || !user.email) {
      throw new Error('Kein Benutzer angemeldet')
    }
    
    // re-authentifiziere den benutzer zuerst mit dem aktuellen passwort
    const credential = EmailAuthProvider.credential(user.email, currentPassword)
    await reauthenticateWithCredential(user, credential)
    
    // aktualisiere das passwort
    await updatePassword(user, newPassword)
    
    return true
  } catch (error) {
    console.error('Error changing password:', error)
    throw error
  }
}

// funktion zum löschen des eigenen accounts
// löscht sowohl das firestore-profil als auch den firebase auth account
// benötigt das aktuelle passwort zur bestätigung
export async function deleteAccount(currentPassword) {
  try {
    // hole die firebase auth instanz
    const auth = getAuth()
    // hole den aktuell angemeldeten benutzer
    const user = auth.currentUser
    
    // prüfe ob ein benutzer angemeldet ist und eine email hat
    if (!user || !user.email) {
      throw new Error('Kein Benutzer angemeldet')
    }
    
    // re-authentifiziere den benutzer zuerst mit dem aktuellen passwort
    const credential = EmailAuthProvider.credential(user.email, currentPassword)
    await reauthenticateWithCredential(user, credential)
    
    // lösche das benutzerprofil aus firestore
    const db = getDb()
    const userRef = doc(db, "user", user.uid)
    await deleteDoc(userRef)
    
    // lösche den firebase auth account
    await deleteUser(user)
    
    return true
  } catch (error) {
    console.error('Error deleting account:', error)
    throw error
  }
}

// cooldown-tracker für verifizierungs-e-mails
// verhindert dass zu viele e-mails gesendet werden und zu viele gültige action codes existieren
// speichert den zeitstempel der letzten gesendeten e-mail pro benutzer im localstorage
const VERIFICATION_COOLDOWN_MS = 60 * 1000 // 60 sekunden cooldown zwischen e-mails

// funktion zum prüfen ob der cooldown für die verifizierungs-e-mail abgelaufen ist
function getVerificationCooldownRemaining(userEmail) {
  try {
    const key = `verification_cooldown_${userEmail}`
    const lastSent = localStorage.getItem(key)
    if (!lastSent) return 0
    
    const elapsed = Date.now() - parseInt(lastSent, 10)
    const remaining = VERIFICATION_COOLDOWN_MS - elapsed
    return remaining > 0 ? remaining : 0
  } catch {
    return 0 // bei fehler (z.b. localstorage blockiert) erlaube senden
  }
}

// funktion zum setzen des cooldown-zeitstempels
function setVerificationCooldown(userEmail) {
  try {
    const key = `verification_cooldown_${userEmail}`
    localStorage.setItem(key, Date.now().toString())
  } catch {
    // localstorage fehler ignorieren
  }
}

// funktion zum senden einer e-mail-verifizierung an den aktuell angemeldeten benutzer
// sendet eine e-mail mit einem link zur e-mail-verifizierung
// beinhaltet einen cooldown-mechanismus um zu viele e-mails zu verhindern
export async function sendVerificationEmail() {
  try {
    // hole die firebase auth instanz
    const auth = getAuth()
    // hole den aktuell angemeldeten benutzer
    const user = auth.currentUser
    
    // prüfe ob ein benutzer angemeldet ist
    if (!user) {
      throw new Error('Kein Benutzer angemeldet')
    }
    
    // lade aktuellen status vom server (nicht aus dem cache)
    await user.reload()
    
    // prüfe ob die e-mail bereits verifiziert ist (nach reload für aktuellen status)
    if (user.emailVerified) {
      throw new Error('E-Mail ist bereits verifiziert')
    }
    
    // prüfe cooldown: verhindere zu häufiges senden
    const cooldownRemaining = getVerificationCooldownRemaining(user.email)
    if (cooldownRemaining > 0) {
      const secondsLeft = Math.ceil(cooldownRemaining / 1000)
      throw new Error(`Bitte warte noch ${secondsLeft} Sekunden, bevor du eine neue E-Mail anforderst.`)
    }
    
    // bestimme die redirect-url basierend auf der aktuellen domain
    const actionCodeSettings = {
      // url nach erfolgreicher verifizierung - die verify-email seite verarbeitet den code
      url: window.location.origin + '/verify-email',
      // die app muss die e-mail-verifizierung behandeln können
      handleCodeInApp: true
    }
    
    // sende die verifizierungs-e-mail mit action code settings
    await sendEmailVerification(user, actionCodeSettings)
    
    // setze cooldown, damit nicht sofort eine neue e-mail gesendet werden kann
    setVerificationCooldown(user.email)
    
    return true
  } catch (error) {
    console.error('Error sending verification email:', error)
    throw error
  }
}

// funktion zum markieren einer übung als abgeschlossen für einen benutzer
// fügt die szenario-id zur liste der abgeschlossenen übungen hinzu
// vergibt automatisch punkte basierend auf dem schwierigkeitsgrad (schwierigkeitsgrad * 1000)
export async function markExerciseAsCompleted(userId, scenarioId) {
  try {
    // hole die firestore-datenbankinstanz
    const db = getDb()
    // referenz zum benutzer-dokument
    const userRef = doc(db, "user", userId)
    // hole das aktuelle dokument
    const userSnap = await getDoc(userRef)
    
    // prüfe ob der benutzer existiert
    if (!userSnap.exists()) {
      throw new Error('Benutzer nicht gefunden')
    }
    
    // hole die aktuellen benutzerdaten
    const userData = userSnap.data()
    const completedExercises = userData.completedExercises || []
    
    // prüfe ob die übung bereits als abgeschlossen markiert ist
    if (!completedExercises.includes(scenarioId)) {
      // lade das szenario um den schwierigkeitsgrad zu bekommen
      const { getScenarioById } = await import('./scenarioService')
      const scenario = await getScenarioById(scenarioId)
      
      // berechne punkte basierend auf schwierigkeitsgrad (schwierigkeitsgrad * 1000)
      const difficulty = scenario?.difficulty || 1
      const points = difficulty * 1000
      
      // füge die szenario-id zur liste hinzu
      completedExercises.push(scenarioId)
      
      // hole aktuelle punkte
      const currentPoints = userData.points || 0
      const newPoints = currentPoints + points
      
      // hole das heutige datum als string (yyyy-mm-dd)
      const today = new Date().toISOString().split('T')[0]
      const dailyPoints = userData.dailyPoints || {}
      const todayPoints = dailyPoints[today] || 0
      // addiere die punkte zu den heutigen punkten
      dailyPoints[today] = todayPoints + points
      
      // behalte nur die letzten 30 tage der täglichen punkte
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
      const filteredDailyPoints = {}
      for (const [date, pts] of Object.entries(dailyPoints)) {
        if (date >= thirtyDaysAgo.toISOString().split('T')[0]) {
          filteredDailyPoints[date] = pts
        }
      }
      
      // speichere die aktualisierte liste und punkte
      await setDoc(userRef, {
        completedExercises: completedExercises,
        points: newPoints,
        dailyPoints: filteredDailyPoints,
        updatedAt: new Date().toISOString()
      }, { merge: true })
    }
    
    // gib das aktualisierte profil zurück
    return await getUserProfile(userId)
  } catch (error) {
    console.error('Error marking exercise as completed:', error)
    throw error
  }
}

// funktion zum markieren einer übung als nicht abgeschlossen für einen benutzer
// entfernt die szenario-id aus der liste der abgeschlossenen übungen
// zieht automatisch die punkte ab, die für diese übung vergeben wurden (schwierigkeitsgrad * 1000)
export async function markExerciseAsIncomplete(userId, scenarioId) {
  try {
    // hole die firestore-datenbankinstanz
    const db = getDb()
    // referenz zum benutzer-dokument
    const userRef = doc(db, "user", userId)
    // hole das aktuelle dokument
    const userSnap = await getDoc(userRef)
    
    // prüfe ob der benutzer existiert
    if (!userSnap.exists()) {
      throw new Error('Benutzer nicht gefunden')
    }
    
    // hole die aktuellen benutzerdaten
    const userData = userSnap.data()
    const completedExercises = userData.completedExercises || []
    
    // prüfe ob die übung überhaupt als abgeschlossen markiert war
    if (completedExercises.includes(scenarioId)) {
      // lade das szenario um den schwierigkeitsgrad zu bekommen
      const { getScenarioById } = await import('./scenarioService')
      const scenario = await getScenarioById(scenarioId)
      
      // berechne punkte basierend auf schwierigkeitsgrad (schwierigkeitsgrad * 1000)
      const difficulty = scenario?.difficulty || 1
      const points = difficulty * 1000
      
      // entferne die szenario-id aus dem array
      const filtered = completedExercises.filter(id => id !== scenarioId)
      
      // hole aktuelle punkte
      const currentPoints = userData.points || 0
      const newPoints = Math.max(0, currentPoints - points) // verhindere negative punkte
      
      // hole das heutige datum als string (yyyy-mm-dd)
      const today = new Date().toISOString().split('T')[0]
      const dailyPoints = userData.dailyPoints || {}
      const todayPoints = dailyPoints[today] || 0
      // ziehe die punkte von den heutigen punkten ab
      dailyPoints[today] = Math.max(0, todayPoints - points)
      
      // behalte nur die letzten 30 tage der täglichen punkte
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
      const filteredDailyPoints = {}
      for (const [date, pts] of Object.entries(dailyPoints)) {
        if (date >= thirtyDaysAgo.toISOString().split('T')[0]) {
          filteredDailyPoints[date] = pts
        }
      }
      
      // speichere die aktualisierte liste ohne die entfernte id und aktualisierte punkte
      await setDoc(userRef, {
        completedExercises: filtered,
        points: newPoints,
        dailyPoints: filteredDailyPoints,
        updatedAt: new Date().toISOString()
      }, { merge: true })
    } else {
      // wenn übung nicht als abgeschlossen markiert war, entferne sie trotzdem aus der liste
      const filtered = completedExercises.filter(id => id !== scenarioId)
      await setDoc(userRef, {
        completedExercises: filtered,
        updatedAt: new Date().toISOString()
      }, { merge: true })
    }
    
    // gib das aktualisierte profil zurück
    return await getUserProfile(userId)
  } catch (error) {
    console.error('Error marking exercise as incomplete:', error)
    throw error
  }
}
